import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiEndpoints, handleApiError } from '../services/api';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../config/api';
import { 
  Calendar,
  Clock,
  Users,
  Plus,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  X
} from 'lucide-react';

function CleanSessionManagement() {
  const { t, i18n } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all', 'OnDemand', 'Daily', 'Weekly'
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [displayMode, setDisplayMode] = useState('cards'); // 'cards' or 'table'
  const [selectedUserType, setSelectedUserType] = useState('all'); // 'all', 'coach', 'owner', 'rider'
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    startTime: '',
    endTime: '',
    type: 'OnDemand', // 'OnDemand', 'Daily', 'Weekly'
    isFixed: true,
    // For weekly sessions
    selectedDays: [], // Array of selected days for weekly sessions
    recurringEndDate: '' // End date for recurring sessions
  });

    const [refreshKey, setRefreshKey] = useState(0);
const daysOfWeek = [
    { value: 'Monday', label: t('sessions.daysOfWeek.monday') },
    { value: 'Tuesday', label: t('sessions.daysOfWeek.tuesday') },
    { value: 'Wednesday', label: t('sessions.daysOfWeek.wednesday') },
    { value: 'Thursday', label: t('sessions.daysOfWeek.thursday') },
    { value: 'Friday', label: t('sessions.daysOfWeek.friday') },
    { value: 'Saturday', label: t('sessions.daysOfWeek.saturday') },
    { value: 'Sunday', label: t('sessions.daysOfWeek.sunday') }
  ];

  useEffect(() => {
    fetchSessions();
    fetchUsers();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch only session templates (recurring sessions), not individual instances
      const response = await apiEndpoints.sessions.getAll({
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter to show only recurring templates (IsRecurring = true)
      const allSessions = response.data?.sessions || [];
      const templates = allSessions.filter(session => session.isRecurring === true);
      setSessions(templates || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error(t('sessions.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Users API response:', response.data);
      // Handle the response format: response.data.users or response.data directly
      const usersData = response.data.users || response.data;
      const usersArray = Array.isArray(usersData) ? usersData : [];
      setUsers(usersArray);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Set empty array on error to prevent filter errors
      setUsers([]);
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.startTime || !formData.endTime) {
      toast.error(t('sessions.fillRequiredFields'));
      return;
    }

    // Validate based on session type
    if (formData.type === 'Weekly' && formData.selectedDays.length === 0) {
      toast.error(t('sessions.selectDayForWeekly'));
      return;
    }

    if (formData.type === 'OnDemand' && !formData.date) {
      toast.error(t('sessions.fillRequiredFields'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (formData.type === 'Weekly') {
        // Create sessions for each selected day
        const today = new Date();
        const createdSessions = [];
        
        for (const dayValue of formData.selectedDays) {
          const dayOfWeek = daysOfWeek.find(d => d.value === dayValue);
          const dayIndex = daysOfWeek.indexOf(dayOfWeek);
          
          // Find next occurrence of this day
          const daysUntilTarget = (dayIndex - today.getDay() + 7) % 7;
          const actualDate = new Date(today);
          actualDate.setDate(today.getDate() + daysUntilTarget);

          const sessionData = {
            title: `${formData.title} - ${dayValue}`,
            description: formData.description || '',
            date: actualDate.toISOString(),
            startTime: `${formData.startTime}:00`,
            endTime: `${formData.endTime}:00`,
            type: formData.type,
            isFixed: formData.isFixed,
            isRecurring: true,
            recurringPattern: 'Weekly',
            recurringEndDate: formData.recurringEndDate ? new Date(formData.recurringEndDate).toISOString() : null
          };

          await axios.post(`${API_BASE_URL}/api/sessions`, sessionData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          createdSessions.push(sessionData.title);
        }
        
        toast.success(`Created ${createdSessions.length} weekly session(s) successfully!`);
      } else {
        // Create single session (OnDemand or Daily)
        const sessionData = {
          title: formData.title,
          description: formData.description || '',
          date: formData.type === 'Daily' ? new Date().toISOString() : new Date(formData.date).toISOString(),
          startTime: `${formData.startTime}:00`,
          endTime: `${formData.endTime}:00`,
          type: formData.type,
          isFixed: formData.isFixed,
          isRecurring: formData.type === 'Daily',
          recurringPattern: formData.type === 'Daily' ? 'Daily' : null,
          recurringEndDate: formData.type === 'Daily' && formData.recurringEndDate ? new Date(formData.recurringEndDate).toISOString() : null
        };

        await axios.post(`${API_BASE_URL}/api/sessions`, sessionData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        toast.success(t('sessions.sessionCreated'));
      }

      setShowCreateModal(false);
      setFormData({
        title: '',
        description: '',
        date: '',
        startTime: '',
        endTime: '',
        type: 'OnDemand',
        isFixed: true,
        selectedDays: [],
        recurringEndDate: ''
      });
      fetchSessions();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      console.error('Error creating session:', error);
      toast.error(t('sessions.failedToCreate'));
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm(t('sessions.confirmDelete'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('sessions.sessionDeleted'));
      fetchSessions();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      console.error('Error deleting session:', error);
      toast.error(t('sessions.failedToDelete'));
    }
  };

  const handleAssignUser = async (sessionId, userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/sessions/${sessionId}/add-participant`, 
        { userId }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(t('sessions.userAssigned'));
      fetchSessions();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      console.error('Error assigning user:', error);
      toast.error(t('sessions.failedToAssign'));
    }
  };

  const handleRemoveUser = async (sessionId, userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/sessions/${sessionId}/remove-participant/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('sessions.userRemoved'));
      fetchSessions();
        setRefreshKey(prev => prev + 1); // Force component refresh
      } catch (error) {
      console.error('Error removing user:', error);
      toast.error(t('sessions.failedToRemove'));
    }
  };

  const openAssignmentModal = (session) => {
    setSelectedSession(session);
    setShowAssignmentModal(true);
    setSelectedUserType('all'); // Reset to all users when opening modal
  };

  const openDetailsModal = (session) => {
    setSelectedSession(session);
    setShowDetailsModal(true);
  };

  const getUserRoleText = (role) => {
    switch (role) {
      case 0: return t('sessions.roles.rider');
      case 1: return t('sessions.roles.owner');
      case 2: return t('sessions.roles.coach');
      case 3: return t('sessions.roles.admin');
      case 4: return t('sessions.roles.superAdmin');
      default: return t('sessions.roles.unknown');
    }
  };

  const getUserRoleColor = (role) => {
    switch (role) {
      case 0: return 'bg-blue-500'; // Rider
      case 1: return 'bg-green-500'; // Owner
      case 2: return 'bg-purple-500'; // Coach
      case 3: return 'bg-orange-500'; // Admin
      case 4: return 'bg-red-500'; // SuperAdmin
      default: return 'bg-gray-500';
    }
  };

  const getFilteredUsers = () => {
    const availableUsers = (Array.isArray(users) ? users : [])
      .filter(user => !selectedSession.participants?.some(p => p.userId === user.id));

    if (selectedUserType === 'all') {
      return availableUsers;
    }

    const roleMap = {
      'rider': 0,
      'owner': 1,
      'coach': 2
    };

    return availableUsers.filter(user => user.role === roleMap[selectedUserType]);
  };

  const getStatusColor = (session) => {
    const now = new Date();
    const sessionDate = new Date(session.date);
    
    if (sessionDate < now) {
      return 'bg-gray-100 text-gray-800';
    } else if (sessionDate.toDateString() === now.toDateString()) {
      return 'bg-blue-100 text-blue-800';
    } else {
      return 'bg-green-100 text-green-800';
    }
  };

  const getStatusText = (session) => {
    const now = new Date();
    const sessionDate = new Date(session.date);
    
    if (sessionDate < now) {
      return t('sessions.status.past');
    } else if (sessionDate.toDateString() === now.toDateString()) {
      return t('sessions.status.today');
    } else {
      return t('sessions.status.upcoming');
    }
  };

  const getSessionTypeText = (type) => {
    // Handle undefined, null, string, and number types
    if (type === undefined || type === null) {
      return t('sessions.types.unknown');
    }
    
    const typeValue = typeof type === 'string' ? parseInt(type) : type;
    
    switch (typeValue) {
      case 0: return t('sessions.types.onDemand');
      case 1: return t('sessions.types.daily');
      case 2: return t('sessions.types.weekly');
      default: return t('sessions.types.unknown');
    }
  };

  const getSessionTypeColor = (type) => {
    // Handle undefined, null, string, and number types
    if (type === undefined || type === null) {
      return 'bg-gray-100 text-gray-800';
    }
    
    const typeValue = typeof type === 'string' ? parseInt(type) : type;
    
    switch (typeValue) {
      case 0: return 'bg-blue-100 text-blue-800';
      case 1: return 'bg-green-100 text-green-800';
      case 2: return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getFilteredSessions = () => {
    if (filterType === 'all') {
      return sessions;
    }
    
    const typeMap = {
      'OnDemand': 0,
      'Daily': 1,
      'Weekly': 2
    };
    
    return sessions.filter(session => {
      const sessionType = typeof session.type === 'string' ? parseInt(session.type) : session.type;
      return sessionType === typeMap[filterType];
    });
  };

  const getSessionCountByType = () => {
    return {
      all: sessions.length,
      OnDemand: sessions.filter(s => {
        const type = typeof s.type === 'string' ? parseInt(s.type) : s.type;
        return type === 0;
      }).length,
      Daily: sessions.filter(s => {
        const type = typeof s.type === 'string' ? parseInt(s.type) : s.type;
        return type === 1;
      }).length,
      Weekly: sessions.filter(s => {
        const type = typeof s.type === 'string' ? parseInt(s.type) : s.type;
        return type === 2;
      }).length
    };
  };

  /* removed early loading return to prevent search bar unmounting 
if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  } 
*/

  return (
    <div className="space-y-6" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} key={refreshKey}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('sessions.sessionManagement')}</h1>
          <p className="text-gray-600">{t('sessions.createAndManage')}</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('sessions.listView')}
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'calendar' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('sessions.calendarView')}
            </button>
          </div>
          
          {/* Display Mode Toggle (only in list view) */}
          {viewMode === 'list' && (
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setDisplayMode('cards')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  displayMode === 'cards' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('sessions.cards')}
              </button>
              <button
                onClick={() => setDisplayMode('table')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  displayMode === 'table' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('sessions.table')}
              </button>
            </div>
          )}
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('sessions.createSession')}
          </button>
        </div>
      </div>

      {/* Session Type Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-3">{t('sessions.filterByType')}</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterType('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'all'
                ? 'bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('sessions.allSessions')} ({getSessionCountByType().all})
          </button>
          <button
            onClick={() => setFilterType('OnDemand')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'OnDemand'
                ? 'bg-blue-600 text-white'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            {t('sessions.onDemand')} ({getSessionCountByType().OnDemand})
          </button>
          <button
            onClick={() => setFilterType('Daily')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'Daily'
                ? 'bg-green-600 text-white'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {t('sessions.daily')} ({getSessionCountByType().Daily})
          </button>
          <button
            onClick={() => setFilterType('Weekly')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'Weekly'
                ? 'bg-purple-600 text-white'
                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
            }`}
          >
            {t('sessions.weekly')} ({getSessionCountByType().Weekly})
          </button>
        </div>
      </div>

      {/* Filter Summary */}
      {filterType !== 'all' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-3 ${
              filterType === 'OnDemand' ? 'bg-blue-500' :
              filterType === 'Daily' ? 'bg-green-500' :
              filterType === 'Weekly' ? 'bg-purple-500' : 'bg-gray-500'
            }`}></div>
            <div>
              <h4 className="text-sm font-medium text-blue-900">
                Showing {getSessionTypeText(filterType === 'OnDemand' ? 0 : filterType === 'Daily' ? 1 : 2)} Sessions
              </h4>
              <p className="text-sm text-blue-700">
                {getFilteredSessions().length} of {sessions.length} total sessions
              </p>
            </div>
            <button
              onClick={() => setFilterType('all')}
              className="ml-auto text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Show All Sessions
            </button>
          </div>
        </div>
      )}

      {/* Sessions Display */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {getFilteredSessions().length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filterType === 'all' ? t('sessions.empty.noSessions') : `${t('sessions.empty.noSessions')} ${filterType.toLowerCase()}`}
            </h3>
            <p className="text-gray-500 mb-4">
              {filterType === 'all' 
                ? t('sessions.empty.noSessionsDescription')
                : `${t('sessions.empty.noSessionsDescription')} ${filterType.toLowerCase()}`
              }
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('sessions.createSession')}
            </button>
          </div>
        ) : displayMode === 'cards' ? (
          /* Cards View */
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {getFilteredSessions().map((session) => (
                <div key={session.id} className={`border rounded-lg p-6 hover:shadow-lg transition-shadow ${getSessionTypeColor(session.type)}`}>
                  {/* Session Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{session.title}</h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSessionTypeColor(session.type)}`}>
                        {getSessionTypeText(session.type)}
                      </span>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(session)}`}>
                      {getStatusText(session)}
                    </span>
                  </div>

                  {/* Session Description */}
                  {session.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{session.description}</p>
                  )}

                  {/* Session Details */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center text-sm text-gray-700">
                      <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                      <span>{new Date(session.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <Clock className="w-4 h-4 mr-2 text-gray-400" />
                      <span>{session.startTime} - {session.endTime}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <Users className="w-4 h-4 mr-2 text-gray-400" />
                      <span>
                        {session.participants?.length || 0}
                        {session.maxParticipants ? `/${session.maxParticipants}` : ''} 
                        participants
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => openDetailsModal(session)}
                      className="flex-1 flex items-center justify-center px-3 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Details
                    </button>
                    <button
                      onClick={() => openAssignmentModal(session)}
                      className="flex-1 flex items-center justify-center px-3 py-2 text-sm text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                    >
                      <Users className="w-4 h-4 mr-1" />
                      Assign
                    </button>
                    <button
                      onClick={() => handleDeleteSession(session.id)}
                      className="flex-1 flex items-center justify-center px-3 py-2 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Table View */
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Participants</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getFilteredSessions().map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{session.title}</div>
                        {session.description && (
                          <div className="text-sm text-gray-500">{session.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSessionTypeColor(session.type)}`}>
                        {getSessionTypeText(session.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                        {new Date(session.date).toLocaleDateString()}
                      </div>
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <Clock className="w-4 h-4 mr-2 text-gray-400" />
                        {session.startTime} - {session.endTime}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(session)}`}>
                        {getStatusText(session)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-2 text-gray-400" />
                        {session.participants?.length || 0} participants
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openDetailsModal(session)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>
                        <button
                          onClick={() => openAssignmentModal(session)}
                          className="text-green-600 hover:text-green-900 flex items-center"
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Assign
                        </button>
                        <button
                          onClick={() => handleDeleteSession(session.id)}
                          className="text-red-600 hover:text-red-900 flex items-center"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Session Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Create Session</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleCreateSession} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('sessions.enterSessionTitle')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Session Type *</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: 'OnDemand' }))}
                      className={`p-2 border-2 rounded-lg text-center transition-colors ${
                        formData.type === 'OnDemand'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-medium text-sm">On-Demand</div>
                      <div className="text-xs text-gray-500">One-time</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: 'Daily' }))}
                      className={`p-2 border-2 rounded-lg text-center transition-colors ${
                        formData.type === 'Daily'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-medium text-sm">Daily</div>
                      <div className="text-xs text-gray-500">Recurring</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: 'Weekly' }))}
                      className={`p-2 border-2 rounded-lg text-center transition-colors ${
                        formData.type === 'Weekly'
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-medium text-sm">Weekly</div>
                      <div className="text-xs text-gray-500">Recurring</div>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('sessions.enterSessionDescription')}
                    rows={3}
                  />
                </div>

                {/* Conditional fields based on session type */}
                {formData.type === 'OnDemand' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                )}

                {formData.type === 'Weekly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Days *</label>
                    <div className="grid grid-cols-7 gap-1">
                      {daysOfWeek.map((day) => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => {
                            const newDays = formData.selectedDays.includes(day.value)
                              ? formData.selectedDays.filter(d => d !== day.value)
                              : [...formData.selectedDays, day.value];
                            setFormData(prev => ({ ...prev, selectedDays: newDays }));
                          }}
                          className={`p-2 border-2 rounded-lg text-center transition-colors ${
                            formData.selectedDays.includes(day.value)
                              ? 'border-purple-500 bg-purple-50 text-purple-700'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className="text-xs font-medium">{day.label.substring(0, 3)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {(formData.type === 'Daily' || formData.type === 'Weekly') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Recurring End Date</label>
                    <input
                      type="date"
                      value={formData.recurringEndDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, recurringEndDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('sessions.optionalEndDate')}
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time *</label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time *</label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Create Session
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Session Details Modal */}
      {showDetails && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Session Details</h2>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{selectedSession.title}</h3>
                  {selectedSession.description && (
                    <p className="text-gray-600 mt-1">{selectedSession.description}</p>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">
                      {new Date(selectedSession.date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">
                      {selectedSession.startTime} - {selectedSession.endTime}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">
                    {selectedSession.participants?.length || 0} participants
                  </span>
                </div>
                
                <div className="pt-4 border-t border-gray-200">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedSession)}`}>
                    {getStatusText(selectedSession)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Assign Users to Session</h2>
                <button
                  onClick={() => setShowAssignmentModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ×
                </button>
              </div>
              
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">{selectedSession.title}</h3>
                <p className="text-sm text-gray-600">{selectedSession.description}</p>
              </div>

              {/* Current Participants */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-3">
                  Current Participants ({selectedSession.participants?.length || 0}
                  {selectedSession.maxParticipants ? `/${selectedSession.maxParticipants}` : ''})
                </h4>
                {selectedSession.participants && selectedSession.participants.length > 0 ? (
                  <div className="space-y-2">
                    {selectedSession.participants.map((participant) => (
                      <div key={participant.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                            <Users className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {participant.user?.firstName} {participant.user?.lastName}
                            </div>
                            <div className="text-xs text-gray-500">{participant.user?.email}</div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveUser(selectedSession.id, participant.userId)}
                          className="text-red-600 hover:text-red-900 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No participants assigned yet.</p>
                )}
              </div>

              {/* Available Users */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Available Users</h4>
                
                {/* User Type Tabs */}
                <div className="flex space-x-1 mb-4 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setSelectedUserType('all')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      selectedUserType === 'all' 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    All Users
                  </button>
                  <button
                    onClick={() => setSelectedUserType('coach')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      selectedUserType === 'coach' 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Coaches
                  </button>
                  <button
                    onClick={() => setSelectedUserType('owner')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      selectedUserType === 'owner' 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Owners
                  </button>
                  <button
                    onClick={() => setSelectedUserType('rider')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      selectedUserType === 'rider' 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Riders
                  </button>
                </div>

                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {getFilteredUsers().map((user) => (
                    <div key={user.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${getUserRoleColor(user.role)}`}>
                          <Users className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                          <div className="text-xs text-gray-400">
                            Role: {getUserRoleText(user.role)}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAssignUser(selectedSession.id, user.id)}
                        className="text-green-600 hover:text-green-900 text-sm"
                      >
                        Assign
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowAssignmentModal(false)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Session Details Modal */}
      {showDetailsModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedSession.title}</h2>
                  <div className="flex items-center space-x-3 mt-2">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getSessionTypeColor(selectedSession.type)}`}>
                      {getSessionTypeText(selectedSession.type)}
                    </span>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedSession)}`}>
                      {getStatusText(selectedSession)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Session Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Session Information</h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <Calendar className="w-5 h-5 mr-3 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">Date</div>
                          <div className="text-sm text-gray-600">{new Date(selectedSession.date).toLocaleDateString()}</div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-5 h-5 mr-3 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">Time</div>
                          <div className="text-sm text-gray-600">{selectedSession.startTime} - {selectedSession.endTime}</div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <Users className="w-5 h-5 mr-3 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">Participants</div>
                          <div className="text-sm text-gray-600">{selectedSession.participants?.length || 0} assigned</div>
                        </div>
                      </div>
                      {selectedSession.maxParticipants && (
                        <div className="flex items-center">
                          <Users className="w-5 h-5 mr-3 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">Max Participants</div>
                            <div className="text-sm text-gray-600">{selectedSession.maxParticipants}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700">
                      {selectedSession.description || 'No description provided for this session.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Participants List */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Assigned Participants</h3>
                {selectedSession.participants && selectedSession.participants.length > 0 ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-3">
                      {selectedSession.participants.map((participant) => {
                        const user = users.find(u => u.id === participant.userId);
                        return user ? (
                          <div key={participant.id} className="flex items-center justify-between bg-white rounded-lg p-3">
                            <div className="flex items-center">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${getUserRoleColor(user.role)}`}>
                                <Users className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {user.firstName} {user.lastName}
                                </div>
                                <div className="text-xs text-gray-500">{user.email}</div>
                                <div className="text-xs text-gray-400">
                                  Role: {getUserRoleText(user.role)}
                                </div>
                              </div>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              participant.status === 'Present' ? 'bg-green-100 text-green-800' :
                              participant.status === 'Absent' ? 'bg-red-100 text-red-800' :
                              participant.status === 'Excused' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {participant.status || 'Pending'}
                            </span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No participants assigned to this session yet.</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    openAssignmentModal(selectedSession);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Manage Participants
                </button>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CleanSessionManagement;
